import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AppFooterCompactComponent, AppFooterComponent } from '@sbe-spinoff-client/app-footer';
import { AppShellResponsiveService } from './app-shell-responsive.service';
import type { ShellNavEntry, ShellNavSection, ShellTab } from './app-shell-nav.type';
import { APP_SHELL_CONFIG } from './app-shell.config';
import { ShellBrandComponent } from './widgets/shell-brand.component';
import { ShellNavReorderListComponent } from './widgets/shell-nav-reorder-list.component';
import { ShellSidenavStateService } from './shell-sidenav-state.service';
import { ThemeService } from './theme.service';
import { ThemeToggleComponent } from './widgets/theme-toggle.component';

export type ShellHeader = 'toolbar' | 'tabs' | 'none';

@Component({
  selector: 'sbe-app-shell',
  imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatTabLink,
    MatTabNav,
    MatTabNavPanel,
    MatToolbarModule,
    RouterLink,
    RouterLinkActive,
    AppFooterComponent,
    AppFooterCompactComponent,
    ShellBrandComponent,
    ShellNavReorderListComponent,
    ThemeToggleComponent,
  ],
  templateUrl: './app-shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppShellComponent {
  private readonly config = inject(APP_SHELL_CONFIG, { optional: true }) ?? {};
  // header="tabs"|"none"도 이 root-singleton 서비스를 주입한다 — 두 모드는 isMobile()을
  // 읽지 않지만, BreakpointObserver.observe() 구독 하나는 앱 전체에서 공유되는 root
  // provider라 비용이 무시할 수준(matchMedia 리스너 1개)이라 모드별로 지연 주입할 만큼의
  // 가치가 없다고 판단해 그대로 둔다.
  protected readonly responsive = inject(AppShellResponsiveService);
  protected readonly sidenavState = inject(ShellSidenavStateService);
  // `providedIn: 'root'` tree-shakable 서비스라, 아무도 주입하지 않으면 생성자가 안 돌아
  // .dark 클래스가 영영 안 붙는다. 이 컴포넌트는 shellBare(header='none'+사이드바 없음)에서도
  // 항상 렌더되므로 여기서 무조건 주입해 생성을 강제한다 — ShellBrandComponent/
  // ThemeToggleComponent는 그 조합에서 둘 다 렌더되지 않아 이 주입 없이는 인스턴스화 지점이
  // 없었다(실측: schoolbell-e/web의 member-picker 팝업이 dark: 클래스를 이미 갖고 있는데도
  // 다크모드가 전혀 적용되지 않았다).
  private readonly theme = inject(ThemeService);

  readonly header = input<ShellHeader>('toolbar');
  readonly title = input<string | null>(null);
  readonly brandLogo = input<string | null>(null);
  readonly homeRoute = input<string | null>(null);
  readonly navItems = input<readonly ShellNavEntry[]>([]);
  readonly tabs = input<readonly ShellTab[]>([]);
  readonly renderFooter = input<boolean | null>(null);
  /**
   * 앱 전역 `AppShellConfig.showThemeToggle` 오버라이드 — `renderFooter`와 동일 패턴.
   * 팝업/리다이렉트 표면(`header='none'`+사이드바 없음)처럼 셸 크롬을 완전히 숨겨야 하는
   * 인스턴스가, 앱 전역 설정을 건드리지 않고 이 화면만 콘텐츠 스트립(다크모드 토글)을
   * 끌 수 있어야 한다.
   */
  readonly renderThemeToggle = input<boolean | null>(null);
  /**
   * navItems 없이 shellSidenavContent 슬롯만으로 사이드바를 채우는 앱(예: `_sidebar.md`를
   * 런타임 파싱하는 markdown-docs)을 위한 오버라이드 — 없으면 navItems가 비었을 때
   * `<mat-sidenav>` 자체가 렌더되지 않아 슬롯에 투영한 콘텐츠도 함께 사라진다.
   */
  readonly hasSidenavContent = input(false);
  /**
   * 압축 푸터(`footerVariant`) 판단 기준 — "사이드바가 있는가"가 아니라 "로그인했는가"다
   * (2026-08-14, 사용자 지적). 이 lib은 auth lib에 의존하지 않으므로 로그인 여부를 직접
   * 알 수 없어, 앱이 자기 auth 서비스에서 계산해 넘긴다(`[loggedIn]="auth.isAuthenticated()"`).
   * `null`(기본값, 안 넘긴 경우)이면 `hasSidenav()`로 대체한다 — 로그인 개념이 없는
   * 앱(문서 사이트 등)을 위한 하위 호환 fallback일 뿐, 로그인 앱은 반드시 넘겨야 한다.
   * sbe-commerce/user-web의 `/market`처럼 로그인해도 그 라우트엔 사이드바가 없는 경우가
   * 있어(navItems가 variant별로 갈림) `hasSidenav()`만으로는 로그인 직후에도 계속 마케팅
   * 푸터가 뜨는 오판이 생긴다 — 실측으로 발견(2026-08-14).
   */
  readonly loggedIn = input<boolean | null>(null);
  /**
   * 압축(`compact`)/풀(`full`) 푸터를 앱이 직접 지정하는 입력 — 넘기면 아래 `loggedIn`/
   * `hasSidenav()` 추론을 완전히 건너뛰고 이 값을 그대로 쓴다. "계산(로그인 여부 등)은
   * 앱이 자기 몫으로 하고 셸엔 최종값만 넘겨라"는 지적(2026-09-21)에 따라 추가한다.
   * 생략하면(기본 `null`) 기존 추론으로 하위 호환한다 — 이미 그 추론에 기대는 기존 앱은
   * 그대로 둔다.
   */
  readonly footerVariant = input<'full' | 'compact' | null>(null);

  protected readonly isMobile = this.responsive.isMobile;
  protected readonly sidenavWidthClass = this.config.sidenavWidthClass ?? 'w-64';
  /**
   * 컨텐츠·`header='tabs'` 헤더가 공유하는 컨테이너 폭 클래스 — 기본 `'max-w-7xl'`.
   * `AppShellConfig`로 앱마다 다르게 설정할 수 있게 뺐다가, 실제로 기본값과 다르게 쓰는
   * 앱이 워크스페이스 전체에 하나도 없어(2026-08-12 검토) 상수로 되돌렸었다. 이후
   * example-web의 Stream 데모 페이지(나란히 비교하는 카드가 넓어 1280px 제한이
   * 답답하다는 지적, 2026-09-21)가 실제 사용처로 생겨 다시 입력으로 뺀다 — 이번엔
   * 사용처가 있으므로 반대 방향(상수화) 리팩터는 하지 않는다. 필요한 앱만
   * `[containerMaxWidthClass]="'max-w-none'"`처럼 넘기고, 나머지는 기본값을 그대로 쓴다.
   *
   * `header='toolbar'`(mat-toolbar)와 `@sbe-spinoff-client/app-footer`는 이
   * `containerMaxWidthClass`를 쓰지 않는다 — 둘 다 사이드바 유무와 무관하게 항상
   * 풀블리드다(2026-08-14, 옵트인 없이 전체 기본값). `containerPaddingClass`(좌우 패딩)만
   * 계속 공유한다.
   */
  readonly containerMaxWidthClass = input('max-w-7xl');
  protected readonly containerPaddingClass = 'px-4 sm:px-6 lg:px-8';
  /**
   * 실제 라우트 콘텐츠(`<ng-container *ngTemplateOutlet="body">`)를 감싸는 4곳(toolbar+사이드바/
   * toolbar 단독/tabs/none, 전부 `containerMaxWidthClass + containerPaddingClass` 다음에
   * 이어붙임)에만 적용하는 상하 여백 — `containerPaddingClass`는 콘텐츠 wrapper뿐 아니라
   * hamburger/다크모드 스트립(`#contentStrip`)과 `header='tabs'` 탭바 컨테이너도 함께 쓰므로,
   * 거기에 상하 여백을 얹으면 탭바가 밀리는 등 의도치 않은 위치가 바뀐다 — 그래서 콘텐츠
   * wrapper 전용 값으로 분리했다(2026-08-28, "콘텐츠 영역 상단이 헤더에 너무 붙어있다"
   * 사용자 지적으로 처음엔 pt만 추가했다가, "4방향 다 있으면 좋겠다"는 후속 지적으로 pb도
   * 추가 — 좌우는 이미 containerPaddingClass가 맡고 있으니 이 필드가 상하만 채우면 결과적으로
   * 4방향 모두 채워진다. 라우트 컴포넌트마다 개별 대응하는 대신 셸 레벨 공통값으로 해결한다.
   * 이미 자체 상하 패딩을 갖고 있던 페이지가 있으면(containerPaddingClass 도입 때와 동일한
   * remediation) 그 페이지에서 자체 패딩을 제거해 이중 여백을 피한다.
   */
  protected readonly contentPaddingYClass = 'pt-4 sm:pt-6 lg:pt-8 pb-4 sm:pb-6 lg:pb-8';
  /**
   * 사이드바 nav 항목(`#sidenavBody`) 스타일 — schoolbell-e/web의 `OrgLayoutComponent`
   * (`apps/schoolbell-e/web/src/app/features/orgs/layout/org-layout.component.html`)와
   * spinoff-platform의 `SpinoffOrgSettingsLayoutComponent`가 이미 쓰던 것과 동일한 밀도로
   * 통일했다(2026-08-28, 사용자 지시 — "사이드바 아이템 스타일을 전역으로 통일, 방향은
   * schoolbell-e"). 예전엔 Material `mat-nav-list`/`mat-list-item`(MDC 기본값 — 48px
   * 한 줄 높이, 24px 아이콘)을 그대로 썼는데, 워크스페이스 안에 이미 있던 저 두 개(둘 다
   * 순수 `<a>`+유틸리티 클래스, 14~16px 텍스트)보다 훨씬 커서 앱마다 사이드바 밀도가
   * 제각각으로 보였다. `routerLinkActive` 배색(`bg-blue-50` 등)은 세 군데 전부 이미
   * 동일했으므로 그대로 재사용한다.
   *
   * 저 두 컴포넌트는 2026-08-29에 이 라이브러리의 `ShellNavSection`(heading/들여쓰기
   * 지원)으로 완전히 흡수되며 삭제됐다 — 이제 밀도·색상 스타일의 유일한 소스는 여기다.
   */
  protected readonly navLinkClass =
    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800';
  /** heading 아래 들여쓴 항목 전용 — navLinkClass의 좌측 패딩(px-3 → pl-5)만 오버라이드.
   * schoolbell-e/web `OrgLayoutComponent`가 쓰던 `py-2 pr-3 pl-5`와 동일 밀도. */
  protected readonly navLinkIndentedClass =
    'flex items-center gap-2 rounded-lg py-2 pr-3 pl-5 text-sm font-medium whitespace-nowrap text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800';
  /** section heading — schoolbell-e/web `OrgLayoutComponent`가 쓰던 것과 동일 클래스. */
  protected readonly navHeadingClass =
    'mt-2 px-3 text-xs font-semibold whitespace-nowrap text-gray-500 uppercase dark:text-gray-400 md:mt-3';
  protected readonly hasSidenav = computed(
    () => this.navItems().length > 0 || this.hasSidenavContent(),
  );
  /** `navItems()` 원소가 heading 섹션인지 판별 — `ShellNavItem`엔 `items` 필드가 없다. */
  protected isNavSection(entry: ShellNavEntry): entry is ShellNavSection {
    return 'items' in entry;
  }
  /**
   * 헤더(mat-toolbar)는 사이드바 유무와 무관하게 항상 풀블리드(엣지투엣지)다(2026-08-14
   * 결정). 예전엔 사이드바가 없을 때만 센터 컨테이너로 감쌌는데, 로그인 전(사이드바 없음)·
   * 후(사이드바 생김)를 같은 세션에서 오가는 앱(sbe-sms/user-web, sbe-commerce/user-web
   * 등)에서 그 전환 때마다 헤더가 센터 ↔ 풀블리드로 라이브로 움직이는 것처럼 보였다.
   * 폭 제한(`containerMaxWidthClass`)은 주지 않아 좌우 끝까지 배경/보더가 이어진다.
   *
   * 패딩은 반드시 `!`(important) 접두사를 붙인 유틸(`!px-4 sm:!px-6 lg:!px-8`)로 준다 —
   * Material 툴바 자체 기본 좌우 패딩(16px)이 일반 Tailwind 유틸(`px-4`)보다 로드 순서상
   * 우선해 지워지지 않는다(실측 확인). 예전엔 이 문제를 `!px-0`으로 지운 뒤 패딩을 주는
   * *다른* 엘리먼트(센터 컨테이너 div)에 얹어 피해갔지만, 지금은 패딩을 mat-toolbar
   * 자신에게 직접 줘야 해서 그 우회가 안 통한다 — `!px-0`과 일반 `px-4`를 같은 엘리먼트에
   * 같이 쓰면 `!important`인 `!px-0`이 반응형 브레이크포인트와 무관하게 항상 이겨서
   * 패딩이 전부 0으로 죽는다(실측: sms/timetable 양쪽에서 헤더가 화면 끝에 완전히
   * 붙는 회귀로 발견, 2026-08-14). 그래서 0을 강제하는 대신 원하는 값 자체를
   * `!important`로 준다 — sm/lg 변형도 Tailwind가 뒤에 미디어쿼리로 이어붙이므로 같은
   * `!important` 안에서 소스 순서로 이긴다.
   *
   * `gap-2`는 브랜드·투영 슬롯(shellToolbarStart/Context/Actions/End)들이 서로 붙지
   * 않도록 강제하는 최소 간격이다 — Material 툴바 자체는 기본 gap을 주지 않는다
   * (contentStrip·tabs 헤더가 이미 쓰는 gap-2와 동일 값으로 통일).
   */
  // sticky top-0 z-10: 사이드바(markdown-docs의 sbe-docs-sidebar 등)가 sticky로 화면에 계속
  // 보이는데 헤더만 페이지와 함께 스크롤되어 사라지면 어색하다 — 헤더도 같이 고정한다.
  // 이 div(#sidenav 템플릿 바깥, `flex min-h-screen flex-col`)는 자체 overflow를 안 걸어서
  // window 스크롤을 그대로 기준으로 삼아 sticky가 별다른 우회 없이 그대로 동작한다(사이드바
  // 쪽은 mat-sidenav 내부 CDK가 자체 overflow를 걸어서 material.scss에 별도 오버라이드가
  // 필요했던 것과 다른 케이스). z-10은 뒤에 오는 mat-sidenav-container/main 콘텐츠가 스크롤로
  // 밑을 지나갈 때 헤더 위로 올라와 덮이지 않게 하기 위함 — mat-sidenav의 모바일 backdrop은
  // CDK Overlay가 별도 최상위 레이어에 붙여 렌더하므로 이 z-index와 충돌하지 않는다.
  protected readonly toolbarClass =
    'sticky top-0 z-10 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 gap-2 !px-4 sm:!px-6 lg:!px-8';
  protected readonly shouldRenderFooter = computed(
    () => this.renderFooter() ?? this.config.renderFooter ?? true,
  );
  /**
   * `footerVariant()` 입력이 있으면 그 값을 그대로 쓰고, 없으면(기본) 로그인했는지
   * (`loggedIn()`, 없으면 `hasSidenav()`로 대체 — 위 입력 주석 참고)로 추론한다 — 압축
   * 푸터(`<app-footer-compact />`) 아니면 3열 마케팅 푸터(`<app-footer />`). 어느 쪽이
   * 뜰지 결정할 뿐, 둘 다 풀블리드(엣지투엣지) 레이아웃이다(2026-08-14, 헤더와 동일하게
   * max-w-7xl 센터 컨테이너 제거). 회사 법정 고지 항목(사업자등록번호·통신판매업 신고번호
   * 등)은 두 변형 모두에 전부 남아있다 — `app-footer-compact.component.ts` 참고.
   */
  protected readonly resolvedFooterVariant = computed<'full' | 'compact'>(
    () => this.footerVariant() ?? ((this.loggedIn() ?? this.hasSidenav()) ? 'compact' : 'full'),
  );

  /** 셸의 공개 입력이 있으면 우선하고, 없으면 앱 전역 기본값(`AppShellConfig`)을 쓴다. */
  protected readonly resolvedLogo = computed(() => this.brandLogo() ?? this.config.brandLogo ?? null);
  protected readonly resolvedLogoDark = computed(() => this.config.brandLogoDark ?? null);
  protected readonly resolvedLogoAlt = computed(() => this.config.brandLogoAlt ?? null);
  protected readonly resolvedLabel = computed(() => this.title() ?? this.config.brandText ?? null);

  /**
   * `header='none'`이고 사이드바가 있는(사이드바 전용) 모드 — 브랜드가 사이드바 상단으로,
   * 햄버거가 콘텐츠 스트립으로 가는 두 배치 결정이 같은 조건을 공유한다. 하나의 시그널로
   * 묶어 둘이 서로 다른 조건으로 어긋나는 것을 막는다.
   */
  private readonly sidenavOnlyMode = computed(() => this.header() === 'none' && this.hasSidenav());

  /** 헤더가 있으면(`toolbar`/`tabs`) 브랜드는 헤더 좌측, 없으면 사이드바 상단에 둔다. */
  protected readonly brandInHeader = computed(() => this.header() !== 'none');
  protected readonly brandInSidenav = this.sidenavOnlyMode;

  /**
   * 헤더가 없는 사이드바 전용 모드에서는 콘텐츠 스트립이 햄버거를 대신 맡는다 — `toolbar`
   * 모드는 완전 숨김 접기라 사이드바 안에 버튼을 두면 접은 뒤 다시 펼 수 없어, 항상
   * 툴바(사이드바 밖)에 둔다.
   */
  protected readonly hamburgerInStrip = this.sidenavOnlyMode;
  protected readonly showThemeToggle = computed(
    () => this.renderThemeToggle() ?? this.config.showThemeToggle ?? false,
  );
  /**
   * `header='toolbar'`에서는 다크모드 토글을 콘텐츠 스트립(별도 행)이 아니라 툴바 자체의
   * 우측 슬롯 클러스터 맨 앞(shellToolbarActions/End보다 먼저)에 직접 그린다 — 알림 종·
   * 프로필 메뉴 등과 같은 행에 있어야 "헤더 우측 첫 항목"으로 보인다는 사용자 요청
   * (2026-08-14). `toolbar`가 아닌 모드(`tabs`/`none`)는 그 슬롯 클러스터 자체가 없어
   * (위 주석 참고) 여전히 콘텐츠 스트립에 남는다.
   */
  protected readonly showThemeToggleInToolbar = computed(
    () => this.showThemeToggle() && this.header() === 'toolbar',
  );
  protected readonly showThemeToggleInStrip = computed(
    () => this.showThemeToggle() && this.header() !== 'toolbar',
  );
  protected readonly showContentStrip = computed(() => this.hamburgerInStrip() || this.showThemeToggleInStrip());

  /** 모바일에서 메뉴 항목을 누르면 오버레이 drawer 를 닫는다. */
  onNavigate(): void {
    this.sidenavState.closeOnNavigate();
  }
}
